import { Card, CardContent } from '@/components/ui/card';
import { UserIcon, AlertTriangle } from 'lucide-react';
import { Calendar, type CalendarItem, type VisibleRange } from '@/components/Calendar';
import type { Task, User } from '@littlebooker/dal';
import { cn } from '@/lib/utils';

interface TasksCalendarProps {
  tasks: Task[];
  userMap: Map<string, User>;
  onTaskClick: (task: Task) => void;
  getStatusColor: (status: string) => string;
  getPriorityColor: (priority: string) => string;
  onVisibleRangeChange?: (range: VisibleRange) => void;
}

export function TasksCalendar({
  tasks,
  userMap,
  onTaskClick,
  getStatusColor,
  getPriorityColor,
  onVisibleRangeChange,
}: TasksCalendarProps) {
  // Check if a task is overdue (past due date and not completed)
  const isTaskOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'done') return false;
    return new Date(task.due_date) < new Date();
  };

  // Convert tasks to CalendarItem format
  const calendarItems: (Task & CalendarItem)[] = tasks.filter((t) => t.due_date);

  const getDateKey = (task: Task): string | null => {
    if (!task.due_date) return null;
    const dueDate = new Date(task.due_date);
    return `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')}`;
  };

  const renderTask = (task: Task) => {
    const assignee = task.assigned_to_user_id ? userMap.get(task.assigned_to_user_id) : null;
    const overdue = isTaskOverdue(task);
    return (
      <Card
        onClick={() => onTaskClick(task)}
        className={cn(
          "cursor-pointer hover:border-primary/50 transition-colors p-1.5",
          overdue && "border-destructive bg-destructive/5"
        )}
      >
        <CardContent className="p-0">
          <div className="space-y-1">
            <div className={cn(
              "text-xs font-medium line-clamp-1",
              overdue && "text-destructive"
            )}>
              {overdue && <AlertTriangle className="h-3 w-3 inline mr-1" />}
              {task.title}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <span
                className={`text-xs px-1 py-0.5 rounded ${getStatusColor(task.status)}`}
              >
                {task.status}
              </span>
              {assignee && (
                <UserIcon className="h-3 w-3 text-muted-foreground" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const tasksWithoutDates = tasks.filter((t) => !t.due_date);

  return (
    <div className="space-y-4">
      <Calendar
        items={calendarItems}
        onItemClick={onTaskClick}
        renderItem={renderTask}
        getDateKey={getDateKey}
        emptyMessage="No tasks"
        onVisibleRangeChange={onVisibleRangeChange}
      />

      {/* Tasks without due dates */}
      {tasksWithoutDates.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Tasks without due dates</h3>
            <div className="space-y-2">
              {tasksWithoutDates.map((task) => {
                const assignee = task.assigned_to_user_id ? userMap.get(task.assigned_to_user_id) : null;
                return (
                  <Card
                    key={task.id}
                    onClick={() => onTaskClick(task)}
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{task.title}</div>
                          {task.description && (
                            <div className="text-sm text-muted-foreground line-clamp-1">
                              {task.description}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-1 rounded ${getStatusColor(task.status)}`}
                          >
                            {task.status}
                          </span>
                          {assignee && (
                            <span className="text-xs text-muted-foreground">
                              <UserIcon className="h-3 w-3 inline mr-1" />
                              {assignee.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
